import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { loadStripe } from '@stripe/stripe-js';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AuthenticationNgRxActions } from '@geekbears/gb-ngrx-auth-basic';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { environment } from '@env/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalService, NotificationsService } from '@geekbears/ngx-gb-ui-kit';

import {
  addCardFailureAction,
  addCardSuccessAction,
  MembershipActions,
  subscribeToPlanFailureAction,
  subscribeToPlanSuccessAction,
  addCardLoadingSelector,
  allPlansLoadingSelector,
  allPlansSuccessSelector,
  payMembershipLoadingSelector,
} from '@modules/company-manager/membership/application';
import { SingUpModalModal } from '@modules/shared/presentation';
import { APP_ROUTES } from '@typings/app-routes.constant';
import { Plan } from '@modules/super-admin/company/domain';
import { AppUser } from '@modules/company-manager/users/domain';

@Component({
  selector: 'app-pick-a-plan',
  templateUrl: './pick-a-plan.page.html',
  styleUrls: ['./pick-a-plan.page.scss'],
})
export class PickAPlanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('templateBottomSheet') TemplateBottomSheet!: TemplateRef<any>;
  @ViewChild('cardelement') cardInfo: any;
  @ViewChild('cardNumber') cardNumberElement: any;
  @ViewChild('cardExpiry') cardExpiryElement: any;
  @ViewChild('cardCvc') cardCvcElement: any;

  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  form!: FormGroup;

  cardError: string | null = '';

  saveCardLoading$: Observable<boolean | any>;
  currentUser!: AppUser;
  paymentMethodForm!: FormGroup;
  stripe: any;
  stripePromise: any;
  elements: any;
  card: any;
  cardNumber: any;
  cardExpiry: any;
  cardCvc: any;
  membershipId!: string;
  checkingStripeToken: boolean = false;
  stripeTokenId!: string;
  cardData: {
    brand: string;
    funding?: string;
    last4?: string;
    date?: string;
  } = {
    brand: '',
    funding: '',
    last4: '',
    date: '',
  };
  memberships$: Observable<Plan[] | null | undefined>;
  membershipsLoading$: Observable<boolean | any>;
  companyName: string = '';
  planSelected!: Plan | null;
  payMembershipLoading$: Observable<boolean | undefined>;
  isButtonDisabled: boolean = false;
  _card: string = '';

  get canDismissCard(): boolean {
    return this.planSelected?.price === 0;
  }

  constructor(
    private store$: Store,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private router: Router,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private bottomSheet: MatBottomSheet
  ) {
    this.payMembershipLoading$ = this.store$
      .select(payMembershipLoadingSelector)
      .pipe(
        map((response) => {
          if (response) this.isButtonDisabled = true;
          return response;
        })
      );

    this.getParams();
    this.saveCardLoading$ = this.store$.select(addCardLoadingSelector).pipe(
      map((response) => {
        if (response) this.isButtonDisabled = true;
        return response;
      })
    );
    this.getCurrentUser();
    this.form = this.fb.group({
      cardHolderName: ['', Validators.required],
      cardNumber: ['', Validators.required],
      mm: ['', Validators.required],
      yy: ['', Validators.required],
      dd: ['', Validators.required],
      cvv: ['', Validators.required],
    });

    this.paymentMethodForm = this.fb.group({
      cardNumber: ['', [Validators.required]],
      nameOfCardHolder: ['', [Validators.required, Validators.minLength(6)]],
      expirationDate: ['', [Validators.required]],
      cvv: ['', [Validators.required]],
    });
    /**
     *
     */
    this.memberships$ = this.store$.select(allPlansSuccessSelector);
    this.membershipsLoading$ = this.store$.select(allPlansLoadingSelector);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.store$.dispatch(MembershipActions.getAllPlans());
    this.onFailureActions();
    this.onCardSuccessfullySaved();
    this.onSubscriptionSuccess();
    this.getCards();
  }

  ngAfterViewInit(): void {}

  onCardSuccessfullySaved() {
    this.actions$
      .pipe(
        ofType(addCardSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          if (action) {
            this.onSuccess();
            this.subscribeToPlan();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  subscribeToPlan() {
    this.store$.dispatch(
      MembershipActions.subscribeToPlan({
        subscriptionData: {
          _planId: this.planSelected?._id || '',
          _userId: this.currentUser.id,
        },
      })
    );
  }

  saveCard(stripeToke: string) {
    if (stripeToke && this.currentUser.id) {
      this.store$.dispatch(
        MembershipActions.addCard({
          userId: this.currentUser.id,
          token: {
            paymentMethodToken: stripeToke,
          },
        })
      );
    }
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(addCardFailureAction, subscribeToPlanFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          this.isButtonDisabled = false;
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  initStripe() {
    this.stripePromise = loadStripe(environment.stripePK).then((res) => {
      this.stripe = res;
      this.elements = this.stripe.elements();

      this.cardNumber = this.elements.create('cardNumber');
      this.cardNumber.mount(this.cardNumberElement.nativeElement);
      this.cardExpiry = this.elements.create('cardExpiry');
      this.cardExpiry.mount(this.cardExpiryElement.nativeElement);
      this.cardCvc = this.elements.create('cardCvc');
      this.cardCvc.mount(this.cardCvcElement.nativeElement);
      let m: any = this;
      this.cardNumber.on('change', function (event: any) {
        if (event?.brand)
          if (event?.brand == 'unknown') {
            m.cardData.brand = '';
          } else {
            m.cardData.brand = event?.brand;
          }

        if (event.complete) {
        }
      });
    });
  }

  createStripeToken = async () => {
    if (this.isButtonDisabled == false && this.checkingStripeToken == false) {
      this.checkingStripeToken = true;
      const { token, error } = await this.stripe.createToken(
        this.cardNumber,
        this.paymentMethodForm.controls['nameOfCardHolder'].value
      );

      if (token) {
        this.checkingStripeToken = false;

        this.cardData = {
          brand: this.cardData.brand,
          funding: token.card.funding,
          last4: token.card.last4,
          date: token.card.exp_month + '/' + token.card.exp_year,
        };

        this.saveCard(token.id);
      } else {
        this.onError(error);
        this.checkingStripeToken = false;
      }
    }
  };

  onError(error: any) {
    if (error.message) {
      this.cardError = error.message;
      setTimeout(() => {
        this.cardError = '';
      }, 4000);
    }
  }

  onSuccess() {
    this.notificationsService.success('Card Added Successfully', {
      duration: 3000,
      icon: 'check-circle',
    });
  }

  showSuccessMessage() {
    this.notificationsService.success('Subscription successfully', {
      duration: 3000,
      icon: 'check-circle',
    });
  }

  getParams() {
    this.activatedRoute.params
      .pipe(map((params) => params))
      .subscribe(async (params) => {
        if (params?.['subscriptionId'])
          this.membershipId = params?.['subscriptionId'];
      })
      .unsubscribe();
  }

  getCurrentUser() {
    this.currentUser = JSON.parse(localStorage.getItem('s_user') || '{}');
    if (this.currentUser?.isSubscriptionBypassed || this.currentUser?.company?.isSubscriptionBypassed) {
      this.router.navigateByUrl(APP_ROUTES.dashboard.fullPath);
    }
  }

  navigateBack() {
    this.router.navigateByUrl(
      APP_ROUTES.dashboard.membership[
        'wallet/:id/:subscription/:process'
      ].fullPath.replace(':id', this.membershipId)
    );
  }

  numberFormat(price: any) {
    return new Intl.NumberFormat('en-US').format(price);
  }

  selectPlan(plan: Plan) {
    this.planSelected = plan;

    // if plan allows card dismissal then perform plan purchase
    if (this.canDismissCard) {
      this.subscribeToPlan();
    } else {
      setTimeout(() => {
        this.initStripe();
      }, 100);
    }
  }

  onSubscriptionSuccess() {
    this.actions$
      .pipe(
        ofType(subscribeToPlanSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          if (action) {
            this.showSuccessMessage();
            this.setSubscription(action.response.data);
            this.openModal();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  openModal() {
    this.modalService
      .open(SingUpModalModal, {
        width: '90%',
        maxWidth: '500px',
        panelClass: 'responsive-success-modal',
      })
      .afterClosed()
      .subscribe((response) => {
        const screenWidth = window.screen.width;
        // console.log('Modal closed. ScreenWidth:', screenWidth);
        if (screenWidth < 680) {
          const url = localStorage.getItem('return_url') || '';
          // console.log('Return URL from localStorage:', url);

          if (url) {
            localStorage.removeItem('return_url');
            localStorage.removeItem('from_mobile');
            // console.log('!!! REDIRECTING TO URL !!! ->', url);
            window.open(url, '_self');
          } else {
            // console.log('No URL found, reloading...');
            window.location.reload();
          }
        } else {
          // console.log('Screen width >= 680, reloading...');
          localStorage.removeItem('from_mobile');
          window.location.reload();
        }
      }),
      takeUntil(this.destroy$);
  }

  setSubscription(subscription: Plan) {
    let userOnStorage = JSON.parse(localStorage.getItem('s_user') || '{}');
    if (userOnStorage) {
      userOnStorage.company._subscription = subscription;
      localStorage.setItem('s_user', JSON.stringify(userOnStorage));
    }
  }

  getCards() {
    this.store$.dispatch(
      MembershipActions.getAllCards({ userId: this.currentUser.id })
    );
  }

  logOut() {
    this.bottomSheet.dismiss();
    // localStorage.clear();
    localStorage.removeItem('s_admin');
    localStorage.removeItem('confirmAccountToken');
    localStorage.removeItem('confirmAccountEmail');
    this.store$.dispatch(AuthenticationNgRxActions.logout());
  }

  openBottomSheet() {
    this.bottomSheet.open(this.TemplateBottomSheet);
  }

  closeTemplateSheetMenu() {
    this.bottomSheet.dismiss();
  }
}
